import type { ConversationSummary } from '@/types/conversation';
import type { TimeGroup, GroupedConversations, ConversationRow } from './types';

const GROUP_ORDER: TimeGroup[] = [
  'Today',
  'Yesterday',
  'Previous 7 days',
  'Older',
];

export function getTimeGroup(isoString: string): TimeGroup {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (date.toDateString() === now.toDateString()) return 'Today';

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  if (diffDays <= 7) return 'Previous 7 days';
  return 'Older';
}

// groupConversations/filterConversations are generic over T (rather than
// hardcoded to ConversationSummary) so the ConversationRow.isLocalOnly flag
// injectActiveConversation sets survives through grouping/filtering —
// history-list.tsx runs both of these AFTER injection, and HistoryRow needs
// the flag intact by the time it reaches onDelete.
export function groupConversations<T extends ConversationSummary>(
  conversations: T[]
): GroupedConversations<T>[] {
  const groups = new Map<TimeGroup, T[]>();

  for (const conv of conversations) {
    // Bucket by LAST MESSAGE activity, not updated_at (which the backend bumps
    // on open/close/summary — so merely opening an old thread would relabel it
    // "Today"). Fall back to updated_at for forward-compat with a backend that
    // predates last_message_at.
    const group = getTimeGroup(conv.last_message_at ?? conv.updated_at);
    if (!groups.has(group)) groups.set(group, []);
    groups.get(group)!.push(conv);
  }

  return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => ({
    group: g,
    items: groups.get(g)!,
  }));
}

/**
 * Ensure the active (current) conversation appears in the list (#83).
 *
 * A brand-new chat has NO backend row until its first message commits — the
 * server defers row creation (keeps never-used chats out of the DB), and the
 * list endpoint filters out `message_count == 0`. So the in-progress chat would
 * be missing from the fetched list entirely until it's messaged AND the list is
 * refetched. We inject a synthetic entry from client state so it shows
 * immediately — flagged `isLocalOnly: true` (see ConversationRow in types.ts)
 * so callers (history-sidebar.tsx / conversation-history-drawer.tsx) know NOT
 * to send a DELETE for it; there's nothing on the server to delete.
 *
 * Dedup is by id: `conversationId` is a client-minted UUIDv7, so once the chat
 * commits, the server row shares the SAME id and the synthetic entry collapses
 * into it (server data wins; we only backfill a title the server lacks) — that
 * backfilled row is NOT marked isLocalOnly, since it's a real server record.
 * Pure — the caller supplies `nowIso` (no clock read here) so it stays testable.
 */
export function injectActiveConversation(
  conversations: ConversationSummary[],
  activeId: string | null | undefined,
  activeTitle: string | null | undefined,
  nowIso: string,
  /** Live last-message snippet for the ACTIVE conversation, from the
   *  in-memory transcript. Interim client-side stand-in for the backend's
   *  `preview` field (only the active conversation has content on the
   *  client); a server-provided preview always wins. */
  activePreview?: string | null
): ConversationRow[] {
  if (!activeId) return conversations;

  const idx = conversations.findIndex((c) => c.id === activeId);
  if (idx !== -1) {
    // Already committed → prefer the server row; only backfill fields the
    // server hasn't produced yet (title before generation, preview before
    // the backend `preview` field ships/deploys). A real server row, so no
    // isLocalOnly flag.
    const server = conversations[idx];
    const backfillTitle = !server.title && activeTitle;
    const backfillPreview = !server.preview && activePreview;
    if (backfillTitle || backfillPreview) {
      const copy = conversations.slice();
      copy[idx] = {
        ...server,
        ...(backfillTitle ? { title: activeTitle } : null),
        ...(backfillPreview ? { preview: activePreview } : null),
      };
      return copy;
    }
    return conversations;
  }

  // Not in the server list yet → prepend a synthetic, local-only entry for
  // the in-progress chat (newest, so it sorts to the top of "Today").
  const synthetic: ConversationRow = {
    id: activeId,
    title: activeTitle ?? null,
    message_count: 0,
    is_active: true,
    created_at: nowIso,
    updated_at: nowIso,
    last_message_at: nowIso,
    // No messages have landed yet, so there's nothing to preview and no
    // handoff could possibly have happened on this not-yet-committed chat.
    preview: null,
    had_handoff: false,
    isLocalOnly: true,
  };
  return [synthetic, ...conversations];
}

/**
 * Backfill missing row previews from the session preview cache
 * (use-conversation-previews.ts). A server-provided preview ALWAYS wins —
 * the cache only fills rows the backend hasn't produced a preview for yet
 * (pre-deploy backend, or the just-left active conversation while its list
 * row refetches). Pure; returns the same array when nothing changes.
 */
export function applyPreviewCache(
  conversations: ConversationSummary[],
  cache: Record<string, string>
): ConversationSummary[] {
  let changed = false;
  const merged = conversations.map((c) => {
    if (c.preview || !cache[c.id]) return c;
    changed = true;
    return { ...c, preview: cache[c.id] };
  });
  return changed ? merged : conversations;
}

/**
 * Client-side case-insensitive filter on conversation title.
 * Returns all conversations if query is empty or whitespace.
 */
export function filterConversations<T extends ConversationSummary>(
  conversations: T[],
  query: string
): T[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return conversations;
  return conversations.filter((c) =>
    (c.title || 'New conversation').toLowerCase().includes(trimmed)
  );
}
